import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { forgotPassword } from '../../api/authApi';

const schema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
});

export default function ForgotPasswordPage() {
  const [successMessage, setSuccessMessage] = useState('');
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onSubmit = async (data) => {
    setApiError('');
    setSuccessMessage('');
    try {
      const response = await forgotPassword({ email: data.email });
      setSuccessMessage(
        response?.data?.message || 'Check your email for a reset link.'
      );
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Something went wrong. Please try again.';
      setApiError(message);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary-600">FounderMind AI</h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-modal p-8">
          {/* Heading */}
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-bold text-neutral-900">Reset your password</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Enter your email and we&apos;ll send you a link to reset your password.
            </p>
          </div>

          {/* Success Alert */}
          {successMessage && (
            <div className="mb-5 flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
              <CheckCircleIcon className="h-5 w-5 text-green-500 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          {/* API Error Alert */}
          {apiError && (
            <div className="mb-5 flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          {!successMessage && (
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  {...register('email')}
                  className={clsx(
                    'w-full border rounded-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition',
                    errors.email
                      ? 'border-red-400 bg-red-50 focus:ring-red-400'
                      : 'border-neutral-300 bg-white'
                  )}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'w-full flex items-center justify-center gap-2 rounded-input px-4 py-2.5 text-sm font-semibold text-white transition',
                  isSubmitting
                    ? 'bg-primary-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                )}
              >
                {isSubmitting && (
                  <svg
                    className="h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                )}
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          {/* Back to login */}
          <div className="mt-6 flex items-center justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-primary-600 transition"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
